/**
 * Billing API client (P5a — invoices + manual provider).
 *
 * Mirrors the unified-envelope conventions used in `cluster-api.js` and
 * `uts-api.js`:
 *   - one axios instance off `envConfig`
 *   - best-effort Bearer attach via the request interceptor (the page route
 *     itself is auth-gated; this is defense in depth)
 *   - `unwrap(response)` resolves `{ success, message, data, ... }` into the
 *     bare `data` payload and throws on `success: false`.
 *
 * P5a contract (auth-api `/api/billing/...`):
 *
 *   GET    /subscription
 *   GET    /invoices?status=&page=&page_size=
 *   GET    /invoices/{id}
 *   GET    /invoices/{id}/download   (application/pdf — responseType:'blob')
 *   POST   /invoices/{id}/email
 *   GET    /payment-methods           (returns [] in P5a)
 *   POST   /payment-methods           (503 in P5a)
 *   DELETE /payment-methods/{id}
 *   POST   /payment-methods/{id}/default
 *   POST   /checkout                  (503 in P5a)
 *   POST   /subscription/cancel
 *   GET    /notification-preferences
 *   PUT    /notification-preferences
 *
 * The two 503 endpoints (`POST /payment-methods` and `POST /checkout`) are
 * special-cased into a typed `BillingProviderNotConfigured` error so the UI
 * can render the friendly "payments coming soon" state without sniffing
 * status codes in the component.
 */
import axios from 'axios'
import store from '@/store'
import { envConfig } from '@/utils/env-config'
import { readToken, ACCESS_TOKEN_KEY } from '@/utils/token-storage'

const BASE_URL = envConfig.VUE_APP_API_BASE_URL || envConfig.VITE_API_BASE_URL
let API_VERSION = envConfig.VUE_APP_API_VERSION || envConfig.VITE_API_VERSION || ''
API_VERSION = API_VERSION.replace(/['"]/g, '').trim()
if (API_VERSION === 'v1' || API_VERSION === '/v1' || API_VERSION === 'api/v1') {
  API_VERSION = ''
}

const billingAxios = axios.create({
  baseURL: API_VERSION ? `${BASE_URL}/api/${API_VERSION}` : `${BASE_URL}/api`,
  timeout: envConfig.VUE_APP_API_TIMEOUT || envConfig.VITE_API_TIMEOUT || 30000,
  headers: {
    'Content-Type': 'application/json'
  }
})

billingAxios.interceptors.request.use((config) => {
  const token = store.state.auth?.accessToken || readToken(ACCESS_TOKEN_KEY)
  if (token) {
    config.headers.Authorization = `Bearer ${token}`
  }
  return config
})

/**
 * Typed error raised when the backend returns 503 from a provider-bound
 * endpoint (P5a `POST /payment-methods` and `POST /checkout`). The envelope
 * body looks like:
 *   { error, message, details: { provider, next_step: "offline_activation" } }
 *
 * Components catch this to render the "payments coming soon" card or to
 * route the user to `/buy` for the existing offline upgrade flow.
 */
export class BillingProviderNotConfigured extends Error {
  constructor(message, details = {}) {
    super(message || 'Payment provider not yet configured')
    this.name = 'BillingProviderNotConfigured'
    this.providerComingSoon = true
    this.provider = details.provider || null
    this.nextStep = details.next_step || details.nextStep || null
    this.detail = details
  }
}

function unwrap(response) {
  const body = response?.data
  if (body && typeof body === 'object' && 'success' in body) {
    if (body.success === false) {
      const err = new Error(body.message || 'Request failed')
      err.response = response
      throw err
    }
    return body.data
  }
  return body
}

/**
 * Translate a 503 response from a provider-bound endpoint into the typed
 * BillingProviderNotConfigured error. Falls through for any other error.
 */
function rethrowProviderUnavailable(error) {
  if (error?.response?.status === 503) {
    const body = error.response.data || {}
    const details = body.details || body.data || {}
    throw new BillingProviderNotConfigured(
      body.message || body.error || 'Payment provider not yet configured',
      details
    )
  }
  throw error
}

/**
 * Stream an invoice PDF as a Blob and trigger a Save-As. We deliberately do
 * NOT open a new tab pointing at the download URL — that would lose the
 * Bearer token from the interceptor. Instead we fetch the blob, hand it to
 * `URL.createObjectURL`, click a hidden anchor, then revoke the URL.
 *
 * Returns the suggested filename so the caller can surface it in a toast.
 */
async function streamPdfDownload(id, suggestedName) {
  const response = await billingAxios.get(
    `/billing/invoices/${encodeURIComponent(id)}/download`,
    { responseType: 'blob' }
  )
  // Prefer a server-supplied filename if Content-Disposition is set.
  let filename = suggestedName || `invoice-${id}.pdf`
  const dispo = response.headers?.['content-disposition'] || ''
  const m = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(dispo)
  if (m && m[1]) {
    try {
      filename = decodeURIComponent(m[1])
    } catch (e) {
      filename = m[1]
    }
  }

  const blob = new Blob([response.data], { type: 'application/pdf' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  a.style.display = 'none'
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  // Defer revoke so Safari has time to start the download.
  setTimeout(() => URL.revokeObjectURL(url), 4000)
  return filename
}

export const billingApi = {
  // ── Subscription ─────────────────────────────────────────────────────────
  async getSubscription() {
    const response = await billingAxios.get('/billing/subscription')
    return unwrap(response)
  },

  async cancelSubscription({ atPeriodEnd = true } = {}) {
    const response = await billingAxios.post('/billing/subscription/cancel', {
      at_period_end: atPeriodEnd
    })
    return unwrap(response)
  },

  // ── Invoices ─────────────────────────────────────────────────────────────
  async listInvoices({ status, page = 1, pageSize = 20 } = {}) {
    const params = { page, page_size: pageSize }
    if (status) params.status = status
    const response = await billingAxios.get('/billing/invoices', { params })
    // Envelope: { success, data: [...], count, meta: { pagination } }
    const body = response?.data
    if (body && typeof body === 'object' && 'success' in body) {
      if (body.success === false) {
        const err = new Error(body.message || 'Failed to load invoices')
        err.response = response
        throw err
      }
      return {
        items: Array.isArray(body.data) ? body.data : [],
        count: body.count ?? (Array.isArray(body.data) ? body.data.length : 0),
        pagination: body.meta?.pagination || null
      }
    }
    return { items: Array.isArray(body) ? body : [], count: 0, pagination: null }
  },

  async getInvoice(id) {
    const response = await billingAxios.get(
      `/billing/invoices/${encodeURIComponent(id)}`
    )
    return unwrap(response)
  },

  /**
   * Trigger a Save-As download of the invoice PDF. Returns the filename used
   * so the caller can show it in a toast (e.g. "Saved invoice-2026-03.pdf").
   */
  async downloadInvoice(id, suggestedName) {
    return streamPdfDownload(id, suggestedName)
  },

  async emailInvoice(id) {
    const response = await billingAxios.post(
      `/billing/invoices/${encodeURIComponent(id)}/email`
    )
    return unwrap(response)
  },

  // ── Payment methods ──────────────────────────────────────────────────────
  async listPaymentMethods() {
    const response = await billingAxios.get('/billing/payment-methods')
    return unwrap(response) || []
  },

  /**
   * In P5a this is hard-wired to return 503. We catch it and throw a typed
   * BillingProviderNotConfigured so the UI can render the friendly
   * "payments coming soon" card without inspecting response codes.
   */
  async addPaymentMethod(payload = {}) {
    try {
      const response = await billingAxios.post('/billing/payment-methods', payload)
      return unwrap(response)
    } catch (error) {
      rethrowProviderUnavailable(error)
    }
  },

  async removePaymentMethod(id) {
    const response = await billingAxios.delete(
      `/billing/payment-methods/${encodeURIComponent(id)}`
    )
    return unwrap(response)
  },

  async setDefaultPaymentMethod(id) {
    const response = await billingAxios.post(
      `/billing/payment-methods/${encodeURIComponent(id)}/default`
    )
    return unwrap(response)
  },

  // ── Checkout (P5a returns 503; route the user to /buy instead) ───────────
  async createCheckout({ planId, billingPeriod } = {}) {
    try {
      const response = await billingAxios.post('/billing/checkout', {
        plan_id: planId,
        billing_period: billingPeriod
      })
      return unwrap(response)
    } catch (error) {
      if (error?.response?.status === 503) {
        const body = error.response.data || {}
        const details = body.details || body.data || {}
        const err = new BillingProviderNotConfigured(
          body.message || 'Self-serve checkout is not live yet',
          details
        )
        // Hint to the caller: route to the existing /buy flow.
        err.routeToBuy = true
        throw err
      }
      throw error
    }
  },

  // ── Notification preferences ─────────────────────────────────────────────
  async getNotificationPrefs() {
    const response = await billingAxios.get('/billing/notification-preferences')
    return unwrap(response) || {}
  },

  async updateNotificationPrefs(payload = {}) {
    const response = await billingAxios.put(
      '/billing/notification-preferences',
      payload
    )
    return unwrap(response)
  }
}

export default billingAxios
